import WALLET_API from 'api/wallets/v1'
import SERVICES_API_V2 from 'api/services/v2'
import renderAmountField from 'components/FormControls/amountField'
import renderSwitcher from 'components/FormControls/switcherField'
import renderInputField from 'components/FormControls/textInput'
import GridSpinner from 'components/UI/GridSpinner'
import { Button, Container, Typography } from 'gu-ui-kit'
import omit from 'lodash/omit'
import formStyles from 'modules/Forms/common-form.module.scss'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { Field, formValueSelector, reduxForm } from 'redux-form'
import validator from 'utils/asyncValidator'
import formatMoney from 'utils/formatMoney'
import { isFloatString } from 'utils/isFloatString'
import shouldAsyncValidate from 'utils/shouldAsyncValidate'
import * as Yup from 'yup'
import { Wallets } from 'modules/Forms/Billing/subcomponents'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'
import { ROUTES } from 'constants/routes'

const validationSchema = Yup.object().shape({
  login: Yup.string().required('common::validation_messages.required'),
  wallet: Yup.object().nullable(),
  wallet_id: Yup.number()
    .typeError('common::validation_messages.required')
    .when(['wallet', 'is_auto_replenishment_active'], {
      is: (wallet, is_auto_replenishment_active) => {
        return !!(is_auto_replenishment_active && wallet?.balance === 0)
      },
      then: (schema) =>
        schema
          .typeError('common::validation_messages.required')
          .test(
            'isZero',
            'autoreplenish::form.fields.wallet_id.errors.zero_balance',
            function test() {
              const { path, createError } = this

              return createError({
                path,
                message: 'autoreplenish::form.fields.wallet_id.errors.zero_balance',
              })
            },
          ),
      otherwise: (schema) =>
        schema
          .typeError('common::validation_messages.required')
          .required('common::validation_messages.required'),
    }),
  min_balance_trigger: Yup.string()
    .test('isFloat', 'common::validation_messages.not_a_number', function test(value) {
      const { path, createError } = this

      return (
        isFloatString(value) ||
        createError({
          path,
          message: 'common::validation_messages.not_a_number',
        })
      )
    })
    .required('common::validation_messages.required'),
  auto_replenishment_amount: Yup.string()
    .test('isFloat', 'common::validation_messages.not_a_number', function test(value) {
      const { path, createError } = this

      return (
        isFloatString(value) ||
        createError({
          path,
          message: 'common::validation_messages.not_a_number',
        })
      )
    })
    .required('common::validation_messages.required'),
  month_limit: Yup.string()
    .test('isFloat', 'common::validation_messages.required', function test(value) {
      const { path, createError } = this

      return (
        !value ||
        isFloatString(value) ||
        createError({
          path,
          message: 'common::validation_messages.not_a_number',
        })
      )
    })
    .nullable(),
  is_auto_replenishment_active: Yup.boolean(),
})

let AutoReplenishForm = ({
  accountId,
  handleSubmit,
  submitting,
  form,
  autofill,
  currentSettings,
}) => {
  const { t } = useTranslation()
  const selector = formValueSelector(form)
  const history = useHistory()
  const pathGen = useGeneratePathI18n()
  const [walletsLoading, setWalletsLoading] = useState(true)
  const [rawWallets, setRawWallets] = useState([])
  const [walletsList, setWalletsList] = useState([])
  const [accountBalance, setAccountBalance] = useState(null)
  const [networkType, setNetworkType] = useState(null)

  const {
    i18n,
    formValues: { login, is_auto_replenishment_active, wallet_id },
  } = useSelector((state) => ({
    i18n: state['app'].i18n,
    formValues: selector(state, 'login', 'is_auto_replenishment_active', 'wallet_id'),
  }))

  const submit = useCallback(
    (values, dispatch) =>
      new Promise((resolve, reject) => {
        dispatch({
          meta: { resolve, reject },
          type: 'AUTOREPLENISH/SUBMIT_SETTINGS',
          payload: {
            accountId: accountId,
            data: {
              ...omit(values, ['wallet']),
              min_balance_trigger: parseFloat(values.min_balance_trigger),
              auto_replenishment_amount: parseFloat(values.auto_replenishment_amount),
              month_limit: parseFloat(values.month_limit),
            },
            formName: form,
          },
        })
      }),
    [history, i18n, networkType],
  )

  useEffect(() => {
    if (walletsList.length === 0) {
      WALLET_API.fetchWallets().then(({ response: { data } }) => {
        setRawWallets(data.filter(({ id }) => currentSettings.available_wallet_ids.includes(id)))

        setWalletsList(
          data
            .filter(({ id }) => currentSettings.available_wallet_ids.includes(id))
            .map((wallet) => ({
              ...wallet,
              value: wallet.id,
            })),
        )
        setWalletsLoading(false)
      })
    } else {
      setWalletsLoading(false)
    }
  }, [setWalletsList, setWalletsLoading, currentSettings, setRawWallets])

  useEffect(() => {
    if (wallet_id && rawWallets.length > 0) {
      autofill(
        'wallet',
        rawWallets.find((wallet) => wallet.id === wallet_id),
      )
    }
  }, [wallet_id, rawWallets])

  useEffect(() => {
    if (!accountId) return

    SERVICES_API_V2.getNetworkAccountById({ accountId })
      .then(({ response, error }) => {
        if (error) {
          throw error
        }

        setAccountBalance(response?.data?.balance)
        setNetworkType(response.data.type)
        autofill('login', response?.data?.login)
      })
      .catch(() => history.push(pathGen(ROUTES.SERVICES)))
  }, [accountId, setAccountBalance])

  return (
    <Container
      display={'flex'}
      flexJustify={'center'}
    >
      <div className={formStyles['formContainer']}>
        <div className={formStyles['formContainer__inner']}>
          <Typography
            variant={'Subtitle-40'}
            component={'h1'}
            className={'margin-bottom-16'}
          >
            {t('autoreplenish::title')}
          </Typography>
          <form
            onSubmit={handleSubmit(submit)}
            noValidate
          >
            <div className={'margin-bottom-16'}>
              <Field
                type="text"
                disabled
                fullWidth
                name="login"
                component={renderInputField}
                label={t('autoreplenish::form.fields.login.label')}
                t={t}
                InputProps={{
                  startAdornment: !login && <GridSpinner width={24} />,
                  endAdornment: accountBalance && (
                    <Typography
                      variant={'Body-25'}
                      noMargin
                      color={'Label-Secondary'}
                      noWrap
                      dangerouslySetInnerHTML={{
                        __html: formatMoney(accountBalance.amount, accountBalance.currency),
                      }}
                    />
                  ),
                }}
              />
            </div>

            <Field
              name={'wallet'}
              hidden
              component={renderInputField}
            />

            <Wallets
              label={t('autoreplenish::form.fields.wallet_id.label')}
              emptyOptionLabel={t('common::select_empty_option')}
              className={'margin-bottom-16'}
              walletsLoading={walletsLoading}
              disabled={walletsLoading}
              wallets={walletsList}
              t={t}
            />

            <div className={'margin-bottom-16'}>
              <Field
                label={t('autoreplenish::form.fields.balance.label')}
                fullWidth
                name="min_balance_trigger"
                type="text"
                component={renderAmountField}
                t={t}
                autocomplete="off"
                parse={(value) => value.replace(/\s/g, '').replace(',', '.')}
                format={(value) => (value === null ? '' : '' + value)}
              />
            </div>

            <div className={'margin-bottom-16'}>
              <Field
                label={t('autoreplenish::form.fields.amount.label')}
                fullWidth
                name="auto_replenishment_amount"
                type="text"
                component={renderAmountField}
                t={t}
                autocomplete="off"
                parse={(value) => value.replace(/\s/g, '').replace(',', '.')}
                format={(value) => (value === null ? '' : '' + value)}
              />
            </div>

            <div className={'margin-bottom-16'}>
              <Field
                label={t('autoreplenish::form.fields.limit.label')}
                fullWidth
                name="month_limit"
                type="text"
                component={renderAmountField}
                t={t}
                autocomplete="off"
                parse={(value) => value.replace(/\s/g, '').replace(',', '.')}
                format={(value) => (value === null ? '' : '' + value)}
              />
            </div>

            <div className={'margin-bottom-16'}>
              <Field
                name="is_auto_replenishment_active"
                checked={is_auto_replenishment_active}
                component={renderSwitcher}
                label={t('autoreplenish::form.fields.active.label', {
                  context: is_auto_replenishment_active ? 'off' : 'on',
                })}
              />
            </div>

            <div className={formStyles['formControl__action-buttons']}>
              <Button
                disabled={submitting}
                onClick={() =>
                  history.push(
                    pathGen(networkType ? ROUTES.SERVICES_NETWORK : ROUTES.SERVICES, {
                      networkType,
                    }),
                  )
                }
                variant="default"
                size="l"
                type="button"
              >
                {t('common::form_buttons.goBack')}
              </Button>

              <Button
                disabled={submitting}
                size="l"
                variant="primary"
                type="submit"
                bgColor="Label-GU-Primary"
              >
                {t('common::form_buttons.save')}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </Container>
  )
}

AutoReplenishForm = reduxForm({
  form: 'AutoReplenishForm',
  shouldAsyncValidate: shouldAsyncValidate,
  asyncValidate: validator(validationSchema),
  updateUnregisteredFields: true,
  enableReinitialize: true,
  keepDirtyOnReinitialize: true,
})(AutoReplenishForm)

AutoReplenishForm.propTypes = {
  accountId: PropTypes.string,
  autofill: PropTypes.func,
  invalid: PropTypes.bool,
  currentSettings: PropTypes.object,
  form: PropTypes.string,
  handleSubmit: PropTypes.func,
  submitting: PropTypes.bool,
}

export default AutoReplenishForm
